import { useCallback } from "react";
import { type MutationHookOptions } from "@apollo/client";
import { SpacesDocument, useCreateSpaceMutation } from "#graphql/supergraphHooks";
import {
  type CreateSpaceInput,
  type CreateSpaceMutation,
  type CreateSpaceMutationVariables,
} from "#graphql/supergraphTypes";

export interface UseCreateSpaceReturn {
  createSpace: (input: CreateSpaceInput) => Promise<CreateSpaceMutation["createSpace"] | null>;
  loading: boolean;
  error: ReturnType<typeof useCreateSpaceMutation>[1]["error"];
}

/**
 * DV-197 — create a Space with its client scoping and initial attribute schema
 * (all-or-nothing on the server). Returns the created Space, or `null` if the
 * mutation returned no data.
 *
 * @example
 * const { createSpace, loading } = useCreateSpace();
 * const space = await createSpace({ name, domain, attributeAssignments: [] });
 */
export const useCreateSpace = (
  options?: MutationHookOptions<CreateSpaceMutation, CreateSpaceMutationVariables>,
): UseCreateSpaceReturn => {
  const [mutate, { loading, error }] = useCreateSpaceMutation({
    // Refresh any mounted Spaces list so it reflects the new Space. The query
    // hooks read `no-cache`, so a cache write wouldn't surface — refetching the
    // active query is the mechanism. Overridable via `options`.
    refetchQueries: [SpacesDocument],
    awaitRefetchQueries: true,
    ...options,
  });

  const createSpace = useCallback(
    async (input: CreateSpaceInput) => {
      const { data } = await mutate({ variables: { input } });
      return data?.createSpace ?? null;
    },
    [mutate],
  );

  return { createSpace, loading, error };
};
